import type { AnyZod } from '../schema/introspect'
import type { CheckJSON, FieldType, JsonValue, RegexJSON } from './types'
import { fromJsonValue, toJsonValue } from './values'

// The constraint table, read in both directions from one place so encode and decode cannot drift.
//
// zod v4 keeps checks in `_zod.def.checks`, each with its own `_zod.def.check` discriminator. The names are
// zod's internals rather than its API: `.min()` on a string is `min_length`, on a number `greater_than`, and
// `.int()` is a `number_format`. Decoding goes back through the public API, where `.min()` covers both.

interface ZodCheckDef {
  check?: string
  format?: string
  minimum?: unknown
  maximum?: unknown
  length?: unknown
  value?: unknown
  inclusive?: boolean
  pattern?: RegExp
  prefix?: string
  suffix?: string
  includes?: string
  error?: unknown
}

const defOf = (check: unknown): ZodCheckDef =>
  ((check as { _zod?: { def?: ZodCheckDef } } | undefined)?._zod?.def ?? {})

/**
 * An author's own message, if they wrote one.
 *
 * zod normalizes a string message into a function, so the string is only reachable by CALLING it. A message
 * the author wrote as a function reads the issue and cannot be replayed here — the call is guarded and the
 * pipeline's own wording applies instead.
 */
export function messageOf(source: { error?: unknown }): string | undefined {
  const { error } = source

  if (typeof error === 'string') {
    return error
  }

  if (typeof error !== 'function') {
    return undefined
  }

  try {
    const produced = (error as (issue: unknown) => unknown)({ code: 'custom', input: undefined })

    return typeof produced === 'string' ? produced : undefined
  } catch {
    return undefined
  }
}

const regexToJson = (pattern: RegExp): RegexJSON => ({ source: pattern.source, flags: pattern.flags })

/** One zod check → its wire form, or `undefined` when it cannot cross (a function-backed check). */
function encodeCheck(check: unknown): CheckJSON | undefined {
  const def = defOf(check)
  const message = messageOf(def)
  const at = (kind: CheckJSON['kind'], value?: JsonValue, exclusive?: true): CheckJSON => ({
    kind,
    ...(value === undefined ? {} : { value }),
    ...(exclusive ? { exclusive } : {}),
    ...(message ? { message } : {}),
  })

  switch (def.check) {
    // strings and arrays
    case 'min_length':
      return at('min', toJsonValue(def.minimum))
    case 'max_length':
      return at('max', toJsonValue(def.maximum))
    case 'length_equals':
      return at('length', toJsonValue(def.length))

    // numbers and bigints
    case 'greater_than':
      return at('min', toJsonValue(def.value), def.inclusive ? undefined : true)
    case 'less_than':
      return at('max', toJsonValue(def.value), def.inclusive ? undefined : true)
    case 'multiple_of':
      return at('multipleOf', toJsonValue(def.value))
    case 'number_format':
      // `.int()` — a format check rather than a range one.
      return def.format === 'safeint' || def.format === 'int32' ? at('int') : undefined

    // the string sub-formats that are checks rather than types
    case 'string_format':
      if (def.format === 'regex' && def.pattern) {
        return at('regex', regexToJson(def.pattern))
      }

      if (def.format === 'starts_with') {
        return at('startsWith', def.prefix ?? '')
      }

      if (def.format === 'ends_with') {
        return at('endsWith', def.suffix ?? '')
      }

      if (def.format === 'includes') {
        return at('includes', def.includes ?? '')
      }

      return undefined

    default:
      return undefined
  }
}

export interface EncodedChecks {
  checks: CheckJSON[]
  unsupported: string[]
}

/** Every check a core schema carries. What cannot cross is NAMED rather than silently dropped. */
export function encodeChecks(core: AnyZod | undefined): EncodedChecks {
  const declared = (core?._zod?.def?.checks as unknown[] | undefined) ?? []
  const checks: CheckJSON[] = []
  const unsupported: string[] = []

  for (const check of declared) {
    const encoded = encodeCheck(check)

    if (encoded) {
      checks.push(encoded)
      continue
    }

    const def = defOf(check)

    unsupported.push(`check:${ def.check ?? 'unknown' }${ def.format ? `/${ def.format }` : '' }`)
  }

  return { checks, unsupported }
}

// A zod schema with the constraint methods the table above needs. Structural, so this file stays independent
// of zod's generics — the methods exist on the string/number/array types that carry these checks.
type Constrainable = {
  min?: (value: never, message?: string) => Constrainable
  max?: (value: never, message?: string) => Constrainable
  gt?: (value: never, message?: string) => Constrainable
  lt?: (value: never, message?: string) => Constrainable
  length?: (value: number, message?: string) => Constrainable
  int?: (message?: string) => Constrainable
  multipleOf?: (value: never, message?: string) => Constrainable
  regex?: (pattern: RegExp, message?: string) => Constrainable
  startsWith?: (value: string, message?: string) => Constrainable
  endsWith?: (value: string, message?: string) => Constrainable
  includes?: (value: string, message?: string) => Constrainable
}

/**
 * Re-apply one wire check to a rebuilt schema.
 *
 * `type` decides nothing about WHICH method to call — `.min()` is `.min()` on a string, a number and an
 * array — it only guards the numeric-only ones. A check whose method is missing on this type is skipped
 * rather than thrown: a schema decoded slightly loose is the documented failure mode.
 */
export function applyCheck(schema: unknown, check: CheckJSON, type: FieldType): unknown {
  const target = schema as Constrainable
  const value = check.value === undefined ? undefined : fromJsonValue(check.value)
  const numeric = type === 'number' || type === 'bigint'
  const call = (
    method: keyof Constrainable,
    arg?: unknown
  ): unknown => {
    const fn = target[method]

    return typeof fn === 'function'
      ? (fn as (...args: unknown[]) => unknown).call(target, ...(arg === undefined ? [] : [arg]), check.message)
      : schema
  }

  switch (check.kind) {
    case 'min':
      return call(check.exclusive && numeric ? 'gt' : 'min', value)
    case 'max':
      return call(check.exclusive && numeric ? 'lt' : 'max', value)
    case 'length':
      return call('length', value)
    case 'int':
      return call('int')
    case 'multipleOf':
      return call('multipleOf', value)
    case 'regex': {
      const { source, flags } = (check.value ?? { source: '', flags: '' }) as unknown as RegexJSON

      return call('regex', new RegExp(source, flags))
    }
    case 'startsWith':
      return call('startsWith', value)
    case 'endsWith':
      return call('endsWith', value)
    case 'includes':
      return call('includes', value)
    default:
      return schema
  }
}
